import { useMemo } from 'react';
import {
    Box,
    Button,
    InputAdornment,
    TextField,
    ToggleButton,
    ToggleButtonGroup,
    Tooltip,
    Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import type {
    SearchFilterField,
    SearchSchema,
    SearchScope,
} from '../api/searchTypes';
import {
    DEFAULTS,
    SECTION_ORDER,
    SECTION_TITLES,
    exclusiveToggle,
    fieldLabel,
    listState,
    optionLabel,
    readList,
    readParam,
    toggleInList,
    withList,
    withParam,
} from '../state/searchParams';
import { isAddressLookup } from '../state/traderRequest';
import { TagChip, traderLabelStyles } from './TagChip';

export interface FilterPanelProps {
    schema: SearchSchema;
    spec: SearchFilterField[];
    params: URLSearchParams;
    // Event and market panels leave both out and behave as before.
    scope?: SearchScope;
    defaults?: Record<string, string>;
    onChange: (next: URLSearchParams) => void;
    onReset: () => void;
}

function SectionTitle({ title, note }: { title: string; note?: string }) {
    return (
        <Box sx={{ mb: 0.75 }}>
            <Typography
                sx={{
                    fontSize: '11px',
                    fontWeight: 700,
                    letterSpacing: '0.03em',
                }}
            >
                {title}
            </Typography>
            {note && (
                <Typography
                    sx={{ fontSize: '10px', color: 'text.disabled', mt: '2px' }}
                >
                    {note}
                </Typography>
            )}
        </Box>
    );
}

function NotAppliedNote() {
    return (
        <Typography
            sx={{ fontSize: '10px', color: 'text.disabled', mt: '2px' }}
        >
            Not applied while looking up an address or @name.
        </Typography>
    );
}

function FieldLabel({
    field,
    scope,
}: {
    field: SearchFilterField;
    scope: SearchScope;
}) {
    return (
        <Tooltip title={field.help ?? ''} placement="top" disableInteractive>
            <Typography
                sx={{ fontSize: '10px', color: 'text.secondary', mb: '2px' }}
            >
                {fieldLabel(field, scope)}
            </Typography>
        </Tooltip>
    );
}

export function FilterPanel({
    schema,
    spec,
    params,
    scope = 'event',
    defaults = DEFAULTS,
    onChange,
    onReset,
}: FilterPanelProps) {
    const theme = useTheme();
    const trader = scope === 'trader';

    const paper = theme.palette.background.paper;
    const mode = theme.palette.mode;
    const traderLabels = useMemo(
        () =>
            traderLabelStyles(
                trader ? schema.trader_labels : undefined,
                paper,
                mode
            ),
        [trader, schema, paper, mode]
    );

    const bySection = useMemo(() => {
        const map = new Map<string, SearchFilterField[]>();
        for (const field of spec) {
            // The query has its own box; a section holding only it stays hidden.
            if (field.key === 'q') continue;
            const section = field.section ?? 'other';
            const list = map.get(section) ?? [];
            list.push(field);
            map.set(section, list);
        }
        return map;
    }, [spec]);

    const sections = useMemo(() => {
        const known = SECTION_ORDER.filter(s => bySection.has(s));
        const rest = [...bySection.keys()].filter(
            s => !SECTION_ORDER.includes(s)
        );
        return [...known, ...rest];
    }, [bySection]);

    const setValue = (key: string, value: string | null) =>
        onChange(withParam(params, key, value, defaults));

    const setList = (key: string, values: string[]) =>
        onChange(withList(params, key, values, defaults));

    const toggle = (key: string, value: string) =>
        onChange(toggleInList(params, key, value, defaults));

    const toggleExclusive = (
        includeKey: string,
        excludeKey: string,
        value: string,
        target: 'include' | 'exclude'
    ) =>
        onChange(
            exclusiveToggle(
                params,
                includeKey,
                excludeKey,
                value,
                target,
                defaults
            )
        );

    const renderField = (field: SearchFilterField) => {
        if (field.type === 'enum') {
            const options = field.options ?? [];
            const current = readParam(params, field.key, defaults);
            const notApplied =
                trader &&
                field.key === 'active_within_days' &&
                isAddressLookup(params);
            return (
                <Box key={field.key} sx={{ mb: 1 }}>
                    <FieldLabel field={field} scope={scope} />
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={current}
                        onChange={(_, next) =>
                            next && setValue(field.key, next)
                        }
                        sx={notApplied ? { opacity: 0.5 } : undefined}
                    >
                        {options.map(option => (
                            <ToggleButton
                                key={option}
                                value={option}
                                sx={{ fontSize: '10px', px: 0.75, py: 0.25 }}
                            >
                                {optionLabel(field, option)}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                    {notApplied && <NotAppliedNote />}
                </Box>
            );
        }

        if (field.type === 'tristate') {
            const current = readParam(params, field.key, defaults);
            return (
                <Box key={field.key} sx={{ mb: 1 }}>
                    <FieldLabel field={field} scope={scope} />
                    <ToggleButtonGroup
                        exclusive
                        size="small"
                        value={current}
                        onChange={(_, next) =>
                            next && setValue(field.key, next)
                        }
                    >
                        {(field.options ?? ['off', 'yes', 'no']).map(option => (
                            <ToggleButton
                                key={option}
                                value={option}
                                sx={{ fontSize: '10px', px: 0.75, py: 0.25 }}
                            >
                                {option}
                            </ToggleButton>
                        ))}
                    </ToggleButtonGroup>
                </Box>
            );
        }

        if (field.type === 'categories') {
            // event_include / event_exclude for events, main_category_include /
            // main_category_exclude for traders: one chip row per pair.
            if (!field.key.endsWith('_include')) return null;
            const includeKey = field.key;
            const excludeKey = field.key.replace(/_include$/, '_exclude');
            const values = field.options ?? [
                ...schema.event_categories,
                schema.no_category_value,
            ];
            return (
                <Box key={field.key} sx={{ mb: 1 }}>
                    <FieldLabel field={field} scope={scope} />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {values.map(value => (
                            <TagChip
                                key={value}
                                label={value}
                                strong
                                state={listState(
                                    params,
                                    includeKey,
                                    excludeKey,
                                    value
                                )}
                                onInclude={v =>
                                    toggleExclusive(
                                        includeKey,
                                        excludeKey,
                                        v,
                                        'include'
                                    )
                                }
                                onExclude={v =>
                                    toggleExclusive(
                                        includeKey,
                                        excludeKey,
                                        v,
                                        'exclude'
                                    )
                                }
                            />
                        ))}
                    </Box>
                </Box>
            );
        }

        if (field.type === 'tags') {
            const isInclude = field.key === 'tags_include';
            if (!isInclude) return null;
            const include = readList(params, 'tags_include');
            const exclude = readList(params, 'tags_exclude');
            // Trader fields list the label names; event fields use the tags.
            const known = field.options ?? schema.polymarket_tags;
            const extras = [...include, ...exclude].filter(
                t => !known.includes(t)
            );
            const values = [...known, ...extras];
            return (
                <Box key={field.key} sx={{ mb: 1 }}>
                    <FieldLabel field={field} scope={scope} />
                    <Box
                        sx={{
                            display: 'flex',
                            flexWrap: 'wrap',
                            gap: 0.5,
                            maxHeight: 132,
                            overflowY: 'auto',
                        }}
                    >
                        {values.map(value => (
                            <TagChip
                                key={value}
                                label={value}
                                color={traderLabels.get(value)?.color}
                                textColor={traderLabels.get(value)?.textColor}
                                description={
                                    traderLabels.get(value)?.description
                                }
                                state={listState(
                                    params,
                                    'tags_include',
                                    'tags_exclude',
                                    value
                                )}
                                onInclude={v =>
                                    toggleExclusive(
                                        'tags_include',
                                        'tags_exclude',
                                        v,
                                        'include'
                                    )
                                }
                                onExclude={v =>
                                    toggleExclusive(
                                        'tags_include',
                                        'tags_exclude',
                                        v,
                                        'exclude'
                                    )
                                }
                            />
                        ))}
                    </Box>
                </Box>
            );
        }

        if (field.type === 'active_list') {
            const selected = readList(params, field.key);
            return (
                <Box key={field.key} sx={{ mb: 1 }}>
                    <FieldLabel field={field} scope={scope} />
                    {selected.length === 0 ? (
                        <Typography
                            sx={{ fontSize: '10px', color: 'text.disabled' }}
                        >
                            Click one on a result to filter by it.
                        </Typography>
                    ) : (
                        <Box
                            sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}
                        >
                            {selected.map(value => (
                                <TagChip
                                    key={value}
                                    label={value}
                                    strong
                                    state="include"
                                    onInclude={v => toggle(field.key, v)}
                                />
                            ))}
                        </Box>
                    )}
                </Box>
            );
        }

        if (field.type === 'enum_multi') {
            const values = field.options ?? [];
            const selected = readList(params, field.key);
            return (
                <Box key={field.key} sx={{ mb: 1 }}>
                    <FieldLabel field={field} scope={scope} />
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                        {values.map(value => (
                            <TagChip
                                key={value}
                                label={value}
                                strong
                                state={
                                    selected.includes(value) ? 'include' : 'off'
                                }
                                onInclude={v => toggle(field.key, v)}
                            />
                        ))}
                    </Box>
                </Box>
            );
        }

        if (field.type === 'date') {
            return (
                <Box key={field.key} sx={{ mb: 1 }}>
                    <FieldLabel field={field} scope={scope} />
                    <TextField
                        type="date"
                        size="small"
                        fullWidth
                        value={params.get(field.key) ?? ''}
                        onChange={e =>
                            setValue(field.key, e.target.value || null)
                        }
                        inputProps={{ sx: { fontSize: '11px', py: 0.75 } }}
                    />
                </Box>
            );
        }

        // A number field with a default (the trader minimum volume) starts at
        // it. Clearing it writes 0, "no minimum"; null would bring the default
        // straight back. A lookup of one trader ignores the default only.
        const hasDefault = defaults[field.key] !== undefined;
        const notApplied =
            trader &&
            hasDefault &&
            params.get(field.key) === null &&
            isAddressLookup(params);
        return (
            <Box key={field.key} sx={{ mb: 1 }}>
                <FieldLabel field={field} scope={scope} />
                <TextField
                    type="number"
                    size="small"
                    fullWidth
                    value={readParam(params, field.key, defaults)}
                    onChange={e =>
                        setValue(
                            field.key,
                            hasDefault
                                ? // The cleared field shows 0, so the next digit
                                  // arrives as "05"; keep what the user meant.
                                  e.target.value.replace(/^0+(?=\d)/, '') || '0'
                                : e.target.value || null
                        )
                    }
                    sx={notApplied ? { opacity: 0.5 } : undefined}
                    inputProps={{
                        min: field.min,
                        max: field.max,
                        step: field.step,
                        sx: { fontSize: '11px', py: 0.75 },
                    }}
                    InputProps={
                        field.unit
                            ? {
                                  endAdornment: (
                                      <InputAdornment position="end">
                                          <Typography sx={{ fontSize: '10px' }}>
                                              {field.unit}
                                          </Typography>
                                      </InputAdornment>
                                  ),
                              }
                            : undefined
                    }
                />
                {notApplied && <NotAppliedNote />}
            </Box>
        );
    };

    return (
        <Box
            sx={{
                border: 1,
                borderColor: 'divider',
                borderRadius: '4px',
                bgcolor: alpha(theme.palette.background.paper, 0.4),
                p: 1.25,
                overflowY: 'auto',
            }}
        >
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    mb: 1,
                }}
            >
                <Typography sx={{ fontSize: '11px', fontWeight: 700 }}>
                    Filters
                </Typography>
                <Button
                    size="small"
                    onClick={onReset}
                    sx={{ fontSize: '10px' }}
                >
                    Reset
                </Button>
            </Box>

            {sections.map(section => (
                <Box key={section} sx={{ mb: 1.5 }}>
                    <SectionTitle
                        title={SECTION_TITLES[section] ?? section}
                        // section_help describes the event sections ("size" sums
                        // event volume); trader fields carry their own help.
                        note={trader ? undefined : schema.section_help[section]}
                    />
                    {(bySection.get(section) ?? []).map(renderField)}
                </Box>
            ))}
        </Box>
    );
}
